/**
 * Normalizes a birth date from either Unix timestamp or ISO 8601 string format.
 * Apple/Cocoa timestamps are seconds since Jan 1, 2001.
 */
export function normalizeBirthDate(birthDate: number | string): Date {
  if (typeof birthDate === "number") {
    const appleEpoch = new Date("2001-01-01T00:00:00Z").getTime();
    return new Date(appleEpoch + birthDate * 1000);
  }
  return new Date(birthDate);
}

/**
 * Calculates age in years from a birth date.
 */
export function calculateAge(birthDate: Date): number {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
}

/**
 * Checks if a birth date is in the future (child has not been born yet).
 */
export function isInFuture(
  birthDate: number | string | null | undefined
): boolean {
  if (!birthDate) return true;
  try {
    const birth = normalizeBirthDate(birthDate);
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    birth.setUTCHours(0, 0, 0, 0);
    return birth > today;
  } catch {
    return false;
  }
}

/**
 * Filters covered persons to only include children who are 21 years old or younger.
 * Also includes children without a birthDate (may be unborn or have missing data).
 */
export function filterChildrenUnder21<T extends { birthDate: number | string }>(
  patients: T[]
): T[] {
  return patients.filter((person) => {
    if (!person.birthDate) return true;
    try {
      const birthDate = normalizeBirthDate(person.birthDate);
      const age = calculateAge(birthDate);
      return age <= 21;
    } catch {
      return true;
    }
  });
}
